import { useCallback, useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { cn } from '@antimatterhq/ui/lib/utils';
import { FormInput } from '../input/FormInput';
import { FormError } from '@/components/form/form';

export type FormMultiValuesProps<T extends Record<string, string | undefined>> =
  {
    values: T[];
    defaultValue: T;
    addButtonText?: string;
    onChange?: React.FormEventHandler<HTMLSpanElement>;
    onBlur?: React.FocusEventHandler<HTMLSpanElement>;
    name: string;
    disabled?: boolean;
    fields?: {
      key: keyof T;
      placeholder?: string;
    }[];
    maxHeight?: number;
  };

export const FormMultiValues = <T extends Record<string, string | undefined>>({
  values,
  addButtonText = 'Add',
  onChange,
  onBlur,
  name,
  disabled = false,
  fields = [
    { key: 'key', placeholder: 'Enter a Key' },
    { key: 'value', placeholder: 'Enter a Value' },
  ],
  defaultValue,
  maxHeight,
}: FormMultiValuesProps<T>) => {
  const reset = useCallback(() => {
    onChange?.({
      target: {
        name,
        value: [defaultValue || { key: '', value: '' }],
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  }, [onChange, name, defaultValue]);

  useEffect(() => {
    if (values.length === 0) {
      reset();
    }
  }, [values, reset]);

  const { isSubmitting } = useFormikContext();

  return (
    <FieldArray name={name}>
      {({ remove, push }) => (
        <>
          <div
            className={cn([
              maxHeight && '-m-2 overflow-y-auto overflow-x-visible p-2',
            ])}
            style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }}
          >
            {values.map((value, index) => (
              <div key={`${name}[${index}]`}>
                <div className="flex flex-row gap-2">
                  {fields.map((field) => (
                    <FormInput
                      key={String(field.key)}
                      data-testid={`${name}-${index}-${String(field.key)}`}
                      type="text"
                      value={value?.[field.key]}
                      onChange={onChange}
                      onBlur={onBlur}
                      className="flex-1"
                      placeholder={field.placeholder}
                      name={`${name}[${index}].${String(field.key)}`}
                      disabled={isSubmitting || disabled}
                    />
                  ))}
                  <Button
                    onClick={() =>
                      values.length === 1 ? reset() : remove(index)
                    }
                    iconOnly
                    leftIcon="antimatterhq:trash"
                    aria-label="Remove"
                    type="button"
                    className="h-[40px]"
                    data-testid={`${name}-${index}-remove`}
                    disabled={isSubmitting || disabled}
                    tabIndex={-1}
                  />
                </div>
                <FormError name={`${name}[${index}]`} />
                <Spacer size="base" />
              </div>
            ))}
          </div>
          <Button
            leftIcon="radix-icons:plus"
            data-testid={`${name}-add`}
            variant="default"
            onClick={() => push(defaultValue)}
            className="mt-2"
            type="button"
            disabled={isSubmitting || disabled}
          >
            {addButtonText}
          </Button>
        </>
      )}
    </FieldArray>
  );
};
